<template>
  <div class="invoice-issued-container">
    <div class="invoice-issued-header">
      <span class="back" @click="$router.push('/invoice/unissued')">&#8592;</span>
      <span class="title">已开票</span>
    </div>
    <div class="invoice-issued-list" ref="listContainer">
      <div v-if="loading && list.length === 0" class="invoice-issued-loading">加载中...</div>
      <div v-else-if="list.length === 0" class="invoice-issued-empty">
        <div class="empty-tip-icon">📄</div>
        <div class="empty-tip-text">暂无已开票账单</div>
      </div>
      <template v-else>
        <div class="invoice-issued-item" v-for="(item,i) in list" :key="item.paymentId || i">
          <div class="invoice-issued-item-left">
            <div class="invoice-issued-item-title">{{ item.title || '商户充值' }}</div>
            <div class="invoice-issued-item-desc">{{ item.desc || formatDate(item.payTime) }}</div>
            <div class="invoice-issued-item-amount">￥{{ formatAmount(item.amount) }}</div>
          </div>
          <div class="invoice-issued-item-right">
            <button class="invoice-issued-btn" @click="download(item)">下载</button>
          </div>
        </div>
        <div v-if="loadingMore" class="invoice-issued-loading-more">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        <div v-if="noMoreData && list.length > 0 && !loadingMore" class="invoice-issued-no-more">没有更多数据了</div>
      </template>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'InvoiceIssued',
  data () {
    return {
      list: [],
      loading: false,
      loadingMore: false,
      noMoreData: false,
      currentPage: 1,
      pageSize: 10,
      currentBusinessId: null,
      scrollTimer: null
    }
  },
  async mounted () {
    await this.fetchBusinessId()
    if (this.currentBusinessId) {
      await this.loadInvoiceList()
    }
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeUnmount () {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  methods: {
    async fetchBusinessId () {
      try {
        // 优先从 localStorage 获取
        const savedBusiness = localStorage.getItem('currentBusiness')
        if (savedBusiness) {
          try {
            const business = JSON.parse(savedBusiness)
            this.currentBusinessId = business.businessId || business.id || null
            if (this.currentBusinessId) return
          } catch (e) {
            console.warn('解析保存的商户信息失败:', e)
          }
        }

        // 从 API 获取商户信息
        let userId = localStorage.getItem('userId')
        if (!userId) {
          const token = localStorage.getItem('token')
          if (token) {
            try {
              const parts = token.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                userId = payload.userId || payload.uid || payload.sub || null
              }
            } catch (e) {
              console.warn('从 token 解析 userId 失败:', e)
            }
          }
        }

        if (!userId) {
          console.warn('无法获取用户ID，无法查询商户信息')
          return
        }

        const merchantRes = await request({
          url: '/market/business/list',
          method: 'post',
          data: { administor: userId },
          timeout: 30000
        })

        const merchantList = merchantRes?.data || []
        if (merchantList.length > 0) {
          this.currentBusinessId = merchantList[0].businessId || merchantList[0].id || null
          // 保存到 localStorage
          if (this.currentBusinessId) {
            localStorage.setItem('currentBusiness', JSON.stringify(merchantList[0]))
          }
        }
      } catch (e) {
        console.error('获取商户ID失败:', e)
      }
    },
    async loadInvoiceList (isLoadMore = false) {
      if (!this.currentBusinessId) {
        console.warn('商户ID不存在，无法加载发票列表')
        return
      }

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.currentPage = 1
        this.noMoreData = false
        this.list = []
      }

      try {
        const response = await request({
          url: '/pay/businessPayment/search/page',
          method: 'post',
          data: {
            businessId: this.currentBusinessId,
            isInvoice: true, // 查询已开票
            paymentStatus: 'PAID' // 只查询已支付的记录
          },
          params: {
            page: this.currentPage,
            size: this.pageSize
          },
          timeout: 30000
        })

        const data = response?.data || []
        const list = Array.isArray(data) ? data : []

        // 格式化数据
        const formattedList = list.map(item => ({
          paymentId: item.paymentId,
          outTradeNo: item.outTradeNo,
          amount: item.amount || 0,
          payTime: item.payTime,
          payChannel: item.payChannel,
          payWay: item.payWay,
          title: this.getPaymentTitle(item),
          desc: this.getPaymentDesc(item)
        }))

        if (isLoadMore) {
          this.list = [...this.list, ...formattedList]
        } else {
          this.list = formattedList
        }

        // 判断是否还有更多数据
        if (formattedList.length < this.pageSize) {
          this.noMoreData = true
        } else {
          this.currentPage++
        }
      } catch (e) {
        console.error('加载已开票列表失败:', e)
        const errorMsg = e?.message || e?.response?.data?.message || '加载已开票列表失败'
        alert(errorMsg)
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    getPaymentTitle (item) {
      const channelMap = {
        'WECHAT': '微信支付',
        'ALIPAY': '支付宝',
        'BALANCE': '余额支付'
      }
      const channel = channelMap[item.payChannel] || '支付'
      return `商户充值 - ${channel}`
    },
    getPaymentDesc (item) {
      if (item.payTime) {
        return this.formatDate(item.payTime)
      }
      return item.outTradeNo || '商户订单'
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      try {
        const date = new Date(dateStr)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },
    formatAmount (amount) {
      if (!amount && amount !== 0) return '0.00'
      // 金额单位是分，需要转换为元
      const yuan = (amount / 100).toFixed(2)
      return yuan
    },
    download (item) {
      // TODO: 实现发票下载功能
      // eslint-disable-next-line no-unused-vars
      console.log('下载发票', item)
      alert('发票下载功能开发中')
    },
    handleScroll () {
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }
      this.scrollTimer = setTimeout(() => {
        if (this.loading || this.loadingMore || this.noMoreData) return

        const container = this.$refs.listContainer
        if (!container) return

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight

        // 距离底部 100px 时加载更多
        if (scrollTop + windowHeight >= documentHeight - 100) {
          this.loadInvoiceList(true)
        }
      }, 200)
    }
  }
}
</script>

<style scoped>
.invoice-issued-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.invoice-issued-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.invoice-issued-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.invoice-issued-list {
  margin: 18px 0 0;
}

.invoice-issued-item {
  background: #fff;
  border-radius: 12px;
  margin: 0 16px 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.invoice-issued-item-left {
  flex: 1;
  min-width: 0;
}

.invoice-issued-item-title {
  font-size: 17px;
  font-weight: 500;
  color: #222;
  margin-bottom: 4px;
}

.invoice-issued-item-desc {
  font-size: 14px;
  color: #888;
  margin-bottom: 4px;
}

.invoice-issued-item-amount {
  font-size: 16px;
  font-weight: bold;
  color: #09bb07;
}

.invoice-issued-item-right {
  margin-left: 12px;
}

.invoice-issued-btn {
  background: #09bb07;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.invoice-issued-btn:active {
  background: #08a006;
  transform: scale(0.98);
}

.invoice-issued-loading,
.invoice-issued-empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-tip-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-tip-text {
  font-size: 14px;
  color: #999;
}

.invoice-issued-loading-more,
.invoice-issued-no-more {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #09bb07;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
